import { config } from '../../../config/config';

export type LoginAsExchangeOutcome = { kind: 'signedIn'; token: string } | { kind: 'refused' };

/** A session token has to look like a JWT; any other answer is refused. */
const looksLikeJWT = (value: string): boolean => /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value);

async function requestExchange(code: string): Promise<LoginAsExchangeOutcome> {
  let response: Response;
  try {
    // No Authorization header: the code is the only credential, and the session that this browser
    // already has must not go with it.
    response = await window.fetch(`${config.fymApiURL(true)}authorization/login-as`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    // The error is not logged: a transport error can quote the request.
    return { kind: 'refused' };
  }
  if (!response.ok) {
    // 401: the code is bad, used or expired. A code lives 60 seconds, so no other failure is
    // worth a second try either.
    return { kind: 'refused' };
  }
  let body: { token?: unknown };
  try {
    body = (await response.json()) as { token?: unknown };
  } catch {
    return { kind: 'refused' };
  }
  if (typeof body.token !== 'string' || !looksLikeJWT(body.token)) {
    return { kind: 'refused' };
  }
  return { kind: 'signedIn', token: body.token };
}

let inFlight: { code: string; promise: Promise<LoginAsExchangeOutcome> } | null = null;

/**
 * Exchanges a login-as code for a session token (`POST /authorization/login-as { code }`).
 *
 * The code is single use, so calls for the same code share one request and one answer.
 */
export function exchangeLoginAsCode(code: string): Promise<LoginAsExchangeOutcome> {
  if (inFlight?.code === code) {
    return inFlight.promise;
  }
  const promise = requestExchange(code);
  inFlight = { code, promise };
  return promise;
}
